// Audit metrics view switch: stored choice and the geometry helpers the
// Rings and Gauges views share. No imports (unit-tested directly).

export type AuditMetricsView = "bars" | "rings" | "gauges";
export const AUDIT_METRICS_VIEWS: { id: AuditMetricsView; label: string }[] = [
  { id: "bars", label: "Bars" },
  { id: "rings", label: "Rings" },
  { id: "gauges", label: "Gauges" },
];
export const AUDIT_METRICS_VIEW_KEY = "amalgamesh.auditMetricsView";

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

function defaultStorage(): KV | null {
  try {
    const ls = (globalThis as { localStorage?: KV }).localStorage;
    return ls ?? null;
  } catch {
    return null;
  }
}

export function readAuditMetricsView(storage: Pick<KV, "getItem"> | null = defaultStorage()): AuditMetricsView {
  try {
    const v = storage?.getItem(AUDIT_METRICS_VIEW_KEY);
    return AUDIT_METRICS_VIEWS.some((o) => o.id === v) ? (v as AuditMetricsView) : "bars";
  } catch {
    return "bars";
  }
}

export function writeAuditMetricsView(v: AuditMetricsView, storage: Pick<KV, "setItem"> | null = defaultStorage()): void {
  try {
    storage?.setItem(AUDIT_METRICS_VIEW_KEY, v);
  } catch {
    /* storage blocked: the choice just is not remembered */
  }
}

/** Percentage (0..100, clamped) to degrees along a sweep. Degrees run clockwise from straight up; a gauge starts at -90 (left) and sweeps 180. */
export function pctToAngle(pct: number, sweep = 180, start = -90): number {
  const p = Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
  return start + (p * sweep) / 100;
}

export function polar(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
}

/** SVG arc path from a0 to a1 degrees (clockwise from up). */
export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const end = Math.min(a1, a0 + 359.99);
  const s = polar(cx, cy, r, a0);
  const e = polar(cx, cy, r, end);
  const large = end - a0 > 180 ? 1 : 0;
  return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
}

/** "12 of 40" plus " \u00b7 N unknown" when N > 0 (Rings and Gauges count line, matching the Bars wording). With `ofKnownPct` (Clock Set only) it leads with "43% of known \u00b7 ". */
export function metricCountLine(yes: number, total: number, unknown: number, ofKnownPct?: number): string {
  const base = `${yes.toLocaleString("en-US")} of ${total.toLocaleString("en-US")}`;
  const line = unknown > 0 ? `${base} \u00b7 ${unknown.toLocaleString("en-US")} unknown` : base;
  return ofKnownPct === undefined ? line : `${ofKnownPct}% of known \u00b7 ${line}`;
}
